<template src="./ArrobaMedellinLiveKitRoom.html"></template>

<script>
import livekitService from "@/services/Livekit";
import livekitClient from "@/utils/livekitClient";

export default {
  name: "ArrobaMedellinLiveKitRoom",
  props: {
    roomName: {
      type: String,
      required: true,
    },
    // Sala principal del networking. Si roomName difiere, estamos en una sub-sala.
    mainRoomName: {
      type: String,
      default: "",
    },
    // Nombre visible de la sala actual (p.ej. "Sala 2" en un breakout)
    roomLabel: {
      type: String,
      default: "",
    },
    // Activa endpoints de networking (moderación por JWT, breakouts, mano)
    networkingMode: {
      type: Boolean,
      default: false,
    },
    participantName: {
      type: String,
      required: true,
    },
    isModerator: {
      type: Boolean,
      default: false,
    },
    isPublicEvent: {
      type: Boolean,
      default: false,
    },
    userId: {
      type: [String, Number],
      default: "",
    },
    // Id del evento cuando la sala es del distrito de eventos. El API lo usa
    // para autorizar al moderador contra el evento en vez del networking.
    eventId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isConnected: false,
      isReconnecting: false,
      isAudioEnabled: false,
      isVideoEnabled: false,
      isScreenShareEnabled: false,
      isRemoteScreenSharing: false,
      isRecording: false,
      participantCount: 0,
      connectionError: null,
      isConnecting: true,
      activeConnectPromise: null,
      connectionAttemptId: 0,
      participants: [],
      showParticipantPanel: false,
      mutingParticipant: null,
      // Levantar la mano / permisos
      handRaised: false,
      raisingHand: false,
      canSpeak: false,
      canShowCamera: false,
      kickingParticipant: null,
      wasKicked: false,
      // Cierre de sub-salas
      closingCountdown: null,
      closingTimer: null,
      // Chat de la sala
      chatMessages: [],
      chatDraft: "",
      showChat: false,
      unreadChat: 0,
      sendingChat: false,
      // Grabación
      togglingRecording: false,
    };
  },
  computed: {
    isInBreakout() {
      return Boolean(
        this.networkingMode &&
          this.mainRoomName &&
          this.roomName !== this.mainRoomName
      );
    },
    raisedHands() {
      return this.participants.filter((p) => p.handRaised);
    },
    /**
     * Base de los payloads de moderación. `eventId` solo viaja en el distrito
     * de eventos; sin él, el API valida contra el networking de `mainRoom`.
     */
    moderationBase() {
      const base = {
        mainRoom: this.mainRoomName || this.roomName,
        roomName: this.roomName,
      };
      if (this.eventId) base.eventId = this.eventId;
      return base;
    },
    isSharingScreen() {
      return this.isScreenShareEnabled || this.isRemoteScreenSharing;
    },
    /**
     * Con pantalla compartida, el asistente ve solo la pantalla: la tira de
     * participantes le robaba ancho y en móvil dejaba la presentación
     * ilegible. El moderador conserva los tiles porque necesita ver la sala
     * mientras presenta.
     */
    hideParticipantTiles() {
      return this.isSharingScreen && !this.isModerator;
    },
  },
  async mounted() {
    // Eventos personalizados desde livekitClient.js para detectar pantalla compartida
    window.addEventListener(
      "livekit-screenshare-started",
      this.handleRemoteScreenShareStarted
    );
    window.addEventListener(
      "livekit-screenshare-stopped",
      this.handleRemoteScreenShareStopped
    );
    window.addEventListener("livekit-data-received", this.handleDataMessage);
    window.addEventListener(
      "livekit-permissions-changed",
      this.handlePermissionsChanged
    );
    window.addEventListener(
      "livekit-participants-changed",
      this.updateParticipantsList
    );
    window.addEventListener(
      "livekit-room-disconnected",
      this.handleRoomDisconnected
    );

    await this.connectToLiveKit();
  },
  beforeDestroy() {
    this.connectionAttemptId += 1;
    this.activeConnectPromise = null;
    this.stopParticipantPolling();
    this.clearClosingCountdown();
    window.removeEventListener(
      "livekit-screenshare-started",
      this.handleRemoteScreenShareStarted
    );
    window.removeEventListener(
      "livekit-screenshare-stopped",
      this.handleRemoteScreenShareStopped
    );
    window.removeEventListener("livekit-data-received", this.handleDataMessage);
    window.removeEventListener(
      "livekit-permissions-changed",
      this.handlePermissionsChanged
    );
    window.removeEventListener(
      "livekit-participants-changed",
      this.updateParticipantsList
    );
    window.removeEventListener(
      "livekit-room-disconnected",
      this.handleRoomDisconnected
    );
    livekitClient.disconnectFromRoom();
  },
  methods: {
    async connectToLiveKit() {
      if (this.activeConnectPromise) {
        return this.activeConnectPromise;
      }

      const attemptId = ++this.connectionAttemptId;

      this.activeConnectPromise = (async () => {
        try {
          this.isConnecting = true;
          this.connectionError = null;
          this.isConnected = false;

          await livekitClient.disconnectFromRoom();
          this.stopParticipantPolling();

          // En networking el token SIEMPRE sale del endpoint verificado de
          // gestor-api (sala principal con cámara para asistentes, sub-salas
          // con publicación completa). Fuera de networking, flujo legacy.
          const options =
            this.networkingMode && this.mainRoomName
              ? { tokenEndpoint: "breakout", mainRoom: this.mainRoomName }
              : {};

          await livekitClient.connectToRoom(
            this.roomName,
            this.participantName,
            this.isModerator,
            this.userId,
            options
          );

          if (attemptId !== this.connectionAttemptId) {
            return;
          }

          this.isConnected = true;
          this.isAudioEnabled = livekitClient.isMicrophoneEnabled();
          this.isVideoEnabled = livekitClient.isCameraEnabled();
          this.participantCount = livekitClient.getParticipantCount();
          this.handRaised = false;
          this.refreshPublishPermissions();

          // El grid se renderiza al salir del estado "conectando";
          // sincronizar los tiles (incluido el propio) en ese momento.
          // isConnecting baja aquí y no solo en el finally: si no, el
          // $nextTick corre antes del re-render y aún no hay grid.
          this.isConnecting = false;
          this.$nextTick(() => {
            livekitClient.syncTiles();
            // Cámaras y pantalla compartida publicadas antes de entrar
            livekitClient.attachExistingTracks();
            if (this.isVideoEnabled) {
              livekitClient.attachLocalVideo();
            }
          });

          this.startParticipantPolling();
        } catch (error) {
          if (attemptId !== this.connectionAttemptId) {
            return;
          }

          // Cupo agotado: gestor-api no emite el token para la persona 21
          if (error?.response?.data?.code === "NETWORKING_FULL") {
            this.connectionError = error.response.data.message;
            this.isConnected = false;
            this.$emit("room-full", error.response.data);
            return;
          }

          this.connectionError = this.parseConnectionError(error);
          this.isConnected = false;
          this.$emit("error", error);
        } finally {
          if (attemptId === this.connectionAttemptId) {
            this.isConnecting = false;
          }
          this.activeConnectPromise = null;
        }
      })();

      return this.activeConnectPromise;
    },
    // ============================================================
    // Señales DataChannel (breakouts + moderación)
    // ============================================================
    handleDataMessage(event) {
      const message = event.detail || {};
      switch (message.type) {
        case "chat":
          this.chatMessages.push({
            from: message.from || "Participante",
            message: String(message.message || ""),
            time: message.time || new Date().toLocaleTimeString().slice(0, 5),
            own: false,
          });
          if (!this.showChat) this.unreadChat += 1;
          this.scrollChatToBottom();
          break;
        case "breakout:move":
        case "breakout:return":
          this.clearClosingCountdown();
          this.$emit("breakout-signal", message);
          break;
        case "breakout:closing":
          this.startClosingCountdown(message.seconds || 30);
          break;
        case "breakout:broadcast":
          this.$buefy.snackbar.open({
            duration: 8000,
            message: `<b>${message.from || "Moderador"}:</b> ${message.message}`,
            type: "is-warning",
            position: "is-top",
            queue: false,
          });
          break;
        case "moderation:grant-publish":
          this.refreshPublishPermissions();
          if (!this.isModerator) {
            if (message.allow) {
              this.handRaised = false;
              this.$buefy.toast.open({
                duration: 5000,
                position: "is-bottom",
                message:
                  "El moderador te dio la palabra: ya puedes activar tu micrófono y cámara",
                type: "is-success",
              });
            } else {
              this.$buefy.toast.open({
                duration: 4000,
                position: "is-bottom",
                message: "El moderador retiró tu permiso para hablar",
                type: "is-warning",
              });
            }
          }
          break;
        case "moderation:kicked":
          this.wasKicked = true;
          this.$buefy.toast.open({
            duration: 5000,
            position: "is-bottom",
            message: "Fuiste retirado de la sala por el moderador",
            type: "is-danger",
          });
          this.$emit("kicked");
          break;
        default:
          break;
      }
    },
    startClosingCountdown(seconds) {
      this.clearClosingCountdown();
      this.closingCountdown = seconds;
      this.closingTimer = setInterval(() => {
        if (this.closingCountdown === null) return;
        this.closingCountdown -= 1;
        if (this.closingCountdown <= 0) {
          this.clearClosingCountdown();
          this.$emit("breakout-signal", {
            type: "breakout:return",
            targetRoom: this.mainRoomName,
          });
        }
      }, 1000);
    },
    clearClosingCountdown() {
      if (this.closingTimer) {
        clearInterval(this.closingTimer);
        this.closingTimer = null;
      }
      this.closingCountdown = null;
    },
    handleRoomDisconnected() {
      // Si estábamos en una sub-sala y el servidor la cerró (deleteRoom),
      // volvemos a la sala principal en lugar de quedar colgados.
      if (this.isInBreakout && !this.wasKicked && this.isConnected) {
        this.isConnected = false;
        this.$emit("breakout-signal", {
          type: "breakout:return",
          targetRoom: this.mainRoomName,
        });
      }
    },
    handlePermissionsChanged() {
      this.refreshPublishPermissions();
    },
    refreshPublishPermissions() {
      this.canSpeak =
        this.isModerator || livekitClient.canPublishSource("microphone");
      this.canShowCamera =
        this.isModerator || livekitClient.canPublishSource("camera");
      // Si perdió el permiso con el micrófono abierto, se refleja el estado real
      this.isAudioEnabled = livekitClient.isMicrophoneEnabled();
      this.isVideoEnabled = livekitClient.isCameraEnabled();
    },
    // ============================================================
    // Levantar la mano
    // ============================================================
    async handleToggleRaiseHand() {
      const identity = livekitClient.getLocalIdentity();
      if (!identity || this.raisingHand) return;
      this.raisingHand = true;
      try {
        const raised = !this.handRaised;
        await livekitService.raiseHand({
          roomName: this.roomName,
          identity,
          raised,
        });
        this.handRaised = raised;
        this.$buefy.toast.open({
          duration: 3000,
          position: "is-bottom",
          message: raised
            ? "Levantaste la mano. El moderador puede darte la palabra."
            : "Bajaste la mano",
          type: raised ? "is-info" : "is-light",
        });
      } catch (error) {
        console.error("[LiveKit] Error al levantar la mano:", error);
        this.$buefy.toast.open({
          duration: 3000,
          position: "is-bottom",
          message: "No fue posible levantar la mano",
          type: "is-danger",
        });
      } finally {
        this.raisingHand = false;
      }
    },
    // ============================================================
    // Chat de la sala (DataChannel)
    // ============================================================
    toggleChat() {
      this.showChat = !this.showChat;
      if (this.showChat) {
        this.unreadChat = 0;
        this.scrollChatToBottom();
      }
    },
    scrollChatToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.chatList;
        if (list) list.scrollTop = list.scrollHeight;
      });
    },
    async sendChat() {
      const text = this.chatDraft.trim();
      if (!text || this.sendingChat) return;
      this.sendingChat = true;
      try {
        const time = new Date().toLocaleTimeString().slice(0, 5);
        await livekitClient.sendChatMessage({
          type: "chat",
          message: text,
          from: this.participantName,
          time,
        });
        this.chatMessages.push({
          from: "Tú",
          message: text,
          time,
          own: true,
        });
        this.chatDraft = "";
        this.scrollChatToBottom();
      } catch (error) {
        console.error("[LiveKit] Error enviando mensaje de chat:", error);
        this.$buefy.toast.open({
          duration: 3000,
          position: "is-bottom",
          message: "No se pudo enviar el mensaje",
          type: "is-danger",
        });
      } finally {
        this.sendingChat = false;
      }
    },
    handleRemoteScreenShareStarted() {
      this.isRemoteScreenSharing = true;
    },
    handleRemoteScreenShareStopped() {
      this.isRemoteScreenSharing = false;
    },
    parseConnectionError(error) {
      if (!error) return "Error desconocido al conectar";
      if (error.message && error.message.includes("permission")) {
        return "Permisos de cámara o micrófono denegados. Por favor, habilítalos en tu navegador.";
      }
      if (error.message && error.message.includes("token")) {
        return "Token de acceso inválido o expirado. Intenta recargar la página.";
      }
      if (error.message && error.message.includes("network")) {
        return "Error de conexión. Verifica tu conexión a internet.";
      }
      if (
        error.message &&
        error.message.includes("could not establish pc connection")
      ) {
        return "No se pudo establecer la conexión multimedia con LiveKit. Esto suele indicar un problema de red, puertos ICE/TURN bloqueados o una configuración incorrecta del servidor de videollamadas.";
      }
      return error.message || "Error al conectar con la sala de video";
    },
    startParticipantPolling() {
      this.pollInterval = setInterval(() => {
        this.participantCount = livekitClient.getParticipantCount();
        this.updateParticipantsList();
        livekitClient.syncTiles();
        // Respaldo por si se pierde el evento de permisos
        this.refreshPublishPermissions();
      }, 2000);
    },
    updateParticipantsList() {
      try {
        const remoteParticipants = livekitClient.getRemoteParticipants();
        this.participants = remoteParticipants.map((participant) => {
          let metadata = {};
          try {
            metadata = JSON.parse(participant.metadata || "{}");
          } catch (e) {
            console.error("Error parsing participant metadata:", e);
          }

          // Detectar estado del micrófono
          let isMicMuted = true;
          for (const [, pub] of participant.audioTrackPublications) {
            if (pub.source === "microphone") {
              isMicMuted = pub.isMuted || !pub.track;
              break;
            }
          }

          // Detectar estado de la cámara
          let isCamMuted = true;
          for (const [, pub] of participant.videoTrackPublications) {
            if (pub.source === "camera") {
              isCamMuted = pub.isMuted || !pub.track;
              break;
            }
          }

          const permissions = participant.permissions || {};
          const publishSources = permissions.canPublishSources || [];
          // TrackSource numérico: 2 = microphone
          const canSpeakGranted = Boolean(
            permissions.canPublish &&
              (!publishSources.length || publishSources.includes(2))
          );

          return {
            identity: participant.identity,
            name:
              metadata.displayName || participant.name || participant.identity,
            isModerator: metadata.isModerator || false,
            sessionId: metadata.sessionId,
            isConnected: participant.isConnected,
            isMicMuted,
            isCamMuted,
            handRaised: Boolean(metadata.handRaised),
            handRaisedAt: metadata.handRaisedAt || null,
            canPublish: Boolean(permissions.canPublish),
            canSpeakGranted,
          };
        });

        // Emitir evento con la lista actualizada de participantes
        this.$emit("participants-updated", this.participants);
      } catch (error) {
        console.error("Error updating participants list:", error);
      }
    },
    getParticipants() {
      return this.participants;
    },
    stopParticipantPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    async handleToggleAudio() {
      try {
        this.isAudioEnabled = await livekitClient.toggleAudio();
      } catch (error) {
        console.error("[LiveKit] Error toggling audio:", error);
        this.notifyPublishBlocked("micrófono");
      }
    },
    async handleToggleVideo() {
      try {
        this.isVideoEnabled = await livekitClient.toggleVideo();
        if (this.isVideoEnabled) {
          this.$nextTick(() => {
            setTimeout(() => {
              livekitClient.attachLocalVideo();
            }, 100);
          });
        } else {
          livekitClient.detachLocalVideo();
        }
      } catch (error) {
        console.error("[LiveKit] Error toggling video:", error);
        this.notifyPublishBlocked("cámara");
      }
    },

    /**
     * Aviso cuando el servidor rechaza publicar.
     *
     * Desde que el token del asistente incluye micrófono y cámara esto no
     * debería ocurrir; queda como red de seguridad para sesiones que sigan
     * vivas con un token viejo (emitido antes del cambio) o para salas a las
     * que la versión anterior de "silenciar sala" les retiró el permiso.
     */
    notifyPublishBlocked(device) {
      this.$buefy.toast.open({
        duration: 5000,
        position: "is-bottom",
        message:
          `No se pudo activar tu ${device}. Vuelve a entrar a la sala; ` +
          "si continúa, pide al moderador que te habilite.",
        type: "is-warning",
      });
    },
    async handleToggleScreenShare() {
      try {
        this.isScreenShareEnabled = await livekitClient.toggleScreenShare();
      } catch (error) {
        console.error("[LiveKit] Error toggling screen share:", error);
      }
    },
    async handleToggleRecord() {
      if (!this.networkingMode) {
        // Flujo legacy (eventos): solo notifica al padre
        this.isRecording = !this.isRecording;
        this.$emit("toggle-record", this.isRecording);
        return;
      }
      if (this.togglingRecording) return;
      this.togglingRecording = true;
      const starting = !this.isRecording;
      try {
        const payload = { ...this.moderationBase };
        if (starting) {
          await livekitService.networkingStartRecording(payload);
        } else {
          await livekitService.networkingStopRecording(payload);
        }
        this.isRecording = starting;
        this.$emit("toggle-record", this.isRecording);
        this.$buefy.toast.open({
          duration: 4000,
          position: "is-bottom",
          message: starting
            ? "Grabación iniciada. Se guardará al finalizar."
            : "Grabación detenida",
          type: starting ? "is-success" : "is-info",
        });
      } catch (error) {
        console.error("[LiveKit] Error en la grabación:", error);
        this.$buefy.toast.open({
          duration: 5000,
          position: "is-bottom",
          message:
            error?.response?.data?.message ||
            `No se pudo ${starting ? "iniciar" : "detener"} la grabación`,
          type: "is-danger",
        });
      } finally {
        this.togglingRecording = false;
      }
    },
    async handleMuteAll() {
      try {
        if (this.networkingMode) {
          // Una sola acción en el servidor: apaga mic y cámara de los
          // asistentes (moderadores intactos). No les retira el permiso:
          // pueden volver a encenderlos.
          const result = await livekitService.networkingMuteAll({
            ...this.moderationBase,
            includeCamera: true,
          });
          const total = result?.affected?.length || 0;
          this.$buefy.toast.open({
            duration: 4000,
            position: "is-bottom",
            message: total
              ? `Se apagaron micrófonos y cámaras de ${total} participante${
                  total === 1 ? "" : "s"
                }`
              : "No hay asistentes que silenciar",
            type: total ? "is-success" : "is-info",
          });
          this.updateParticipantsList();
        } else {
          await livekitClient.muteAllRemoteParticipants();
        }
        this.$emit("mute-all");
      } catch (error) {
        console.error("[LiveKit] Error muting all:", error);
        this.$buefy.toast.open({
          duration: 4000,
          position: "is-bottom",
          message:
            error?.response?.data?.message || "Error al silenciar la sala",
          type: "is-danger",
        });
      }
    },
    async handleMuteParticipant(identity, currentlyMuted, source) {
      // Solo se apaga: encender el micrófono o la cámara de otro no está
      // permitido por el servidor de LiveKit y queda en manos del participante
      if (currentlyMuted) return;
      this.mutingParticipant = `${identity}-${source}`;
      try {
        if (this.networkingMode) {
          await livekitService.networkingMuteParticipant({
            ...this.moderationBase,
            identity,
            muted: !currentlyMuted,
            source,
          });
        } else {
          await livekitService.muteParticipant({
            roomName: this.roomName,
            identity,
            muted: !currentlyMuted,
            source,
          });
        }
      } catch (error) {
        console.error(
          `[LiveKit] Error toggling ${source} for ${identity}:`,
          error
        );
        this.$buefy.toast.open({
          duration: 3000,
          position: "is-bottom",
          message: `Error al cambiar ${
            source === "audio" ? "micrófono" : "cámara"
          } del participante`,
          type: "is-danger",
        });
      } finally {
        this.mutingParticipant = null;
      }
    },
    toggleParticipantPanel() {
      this.showParticipantPanel = !this.showParticipantPanel;
    },
    async handleKickParticipant(participant) {
      const name = participant.name || participant.identity;
      this.$buefy.dialog.confirm({
        title: "Expulsar participante",
        message: `¿Seguro que quieres expulsar a <b>${name}</b> de la sala?`,
        confirmText: "Expulsar",
        cancelText: "Cancelar",
        type: "is-danger",
        hasIcon: true,
        onConfirm: async () => {
          this.kickingParticipant = participant.identity;
          try {
            if (this.networkingMode) {
              await livekitService.networkingKickParticipant({
                ...this.moderationBase,
                identity: participant.identity,
              });
            } else {
              await livekitService.kickParticipant({
                roomName: this.roomName,
                participantSid: participant.identity,
              });
            }
            this.$buefy.toast.open({
              duration: 3000,
              position: "is-bottom",
              message: `${name} fue expulsado de la sala`,
              type: "is-success",
            });
            this.updateParticipantsList();
          } catch (error) {
            console.error("[LiveKit] Error kicking participant:", error);
            this.$buefy.toast.open({
              duration: 3000,
              position: "is-bottom",
              message: "Error al expulsar participante",
              type: "is-danger",
            });
          } finally {
            this.kickingParticipant = null;
          }
        },
      });
    },
    async handleDisconnect() {
      try {
        await livekitClient.disconnectFromRoom();
        this.isConnected = false;
        this.$emit("disconnected");
      } catch (error) {
        console.error("[LiveKit] Error disconnecting:", error);
      }
    },
    async retryConnection() {
      await this.connectToLiveKit();
    },
  },
  watch: {
    // La vista contenedora (Meet, Event) usa esto para plegar su panel de
    // participantes mientras haya pantalla compartida.
    isSharingScreen(sharing) {
      this.$emit("screen-share-changed", sharing);
    },
    participantCount(newCount) {
      this.$emit("participant-count", newCount);
    },
    // Cambiar la prop roomName reconecta automáticamente (breakouts).
    // El chat es por sala: se limpia al cambiar.
    roomName(newRoom, oldRoom) {
      if (newRoom && newRoom !== oldRoom) {
        this.clearClosingCountdown();
        this.chatMessages = [];
        this.unreadChat = 0;
        this.isRecording = false;
        // Un mute recién hecho no debe dejar deshabilitado el panel de la sala nueva
        this.mutingParticipant = null;
        this.connectToLiveKit();
      }
    },
    raisedHands(newHands, oldHands) {
      if (!this.isModerator) return;
      const previous = new Set((oldHands || []).map((p) => p.identity));
      const fresh = newHands.filter((p) => !previous.has(p.identity));
      for (const participant of fresh) {
        this.$buefy.toast.open({
          duration: 5000,
          position: "is-top",
          message: `✋ ${participant.name} levantó la mano`,
          type: "is-info",
        });
      }
      this.$emit("raised-hands", newHands);
    },
  },
};
</script>

<style lang="scss" src="./ArrobaMedellinLiveKitRoom.scss"></style>
